import { ref, type Ref } from "vue"
import { useRouter } from "vue-router"
import { useApi } from "./useApi"

interface PaginationLinks {
  next: string | null
  previous: string | null
}

interface PaginationContent {
  current: number
  last_page: number
}

interface JobListOptions {
  baseUrl: string
  routeName: string
  auth?: boolean
}

interface UseJobListReturn {
  jobs: Ref<any[]>
  links: Ref<PaginationLinks | null>
  content: Ref<PaginationContent | null>
  count: Ref<number>
  loading: Ref<boolean>
  error: Ref<string | null>
  getJobs: (
    page?: number | null,
    extraParams?: Record<string, any>,
  ) => Promise<void>
}

export function useJobList(options: JobListOptions): UseJobListReturn {
  const router = useRouter()
  const { loading, error, execute } = useApi()

  const jobs = ref<any[]>([])
  const links = ref<PaginationLinks | null>(null)
  const content = ref<PaginationContent | null>(null)
  const count = ref(0)

  async function getJobs(
    page: number | null = null,
    extraParams: Record<string, any> = {},
  ): Promise<void> {
    const params: Record<string, any> = { ...extraParams }
    if (page !== null) {
      params.page = page
    }

    const result = await execute(options.baseUrl, {
      params,
      auth: options.auth ?? false,
    })

    if (result) {
      jobs.value = result.results ?? []
      links.value = result.links ?? null
      content.value = result.pages ?? null
      count.value = result.count ?? 0
    }

    router.replace({ name: options.routeName, query: params }).catch(() => {})
  }

  return { jobs, links, content, count, loading, error, getJobs }
}
